﻿import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { useFormik } from 'formik';
import { Container, Row, Col, Card, Alert, Button, Spinner } from 'react-bootstrap';
import { SelectOption } from './inputs/SelectInput';
import { RadioGroup } from './inputs/RadioGroup';
import { 
    getGrecaptcha, 
    waitForRecaptcha, 
    Locale, 
    Retailer, 
    Courier, 
    FormErrors, 
    FormValues 
} from './submit/types';
import { RetailerFields } from './submit/RetailerFields';
import { CourierFields } from './submit/CourierFields';
import { LocaleFields } from './submit/LocaleFields';

interface SubmitFormProps {
    recaptchaSiteKey: string;
    initialValues?: Partial<FormValues>;
    initialErrors?: FormErrors;
}

const SubmitForm: React.FC<SubmitFormProps> = ({
    recaptchaSiteKey,
    initialValues: initialValuesProp,
    initialErrors
}) => {
    // Process initial errors: move RecaptchaResponse errors to _general
    const processedInitialErrors = useMemo(() => {
        if (!initialErrors) return {};
        
        const processed: FormErrors = { ...initialErrors };
        
        if (processed.RecaptchaResponse) {
            if (!processed._general) {
                processed._general = [];
            }
            processed._general.push(...processed.RecaptchaResponse);
            delete processed.RecaptchaResponse;
        }
        
        return processed;
    }, [initialErrors]);
    
    const [serverErrors, setServerErrors] = useState<FormErrors>(processedInitialErrors);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    // Helper function to add a hidden input field to a form
    const addFormField = (form: HTMLFormElement, name: string, value: string | null | undefined): void => {
        if (value == null || value === '') return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    };

    // Helper function to add multiple values for the same field name
    const addFormFields = (form: HTMLFormElement, name: string, values: string[]): void => {
        if (!values || values.length === 0) return;
        values.forEach(value => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        });
    };

    const formik = useFormik<FormValues>({
        initialValues: {
            submissionType: initialValuesProp?.submissionType || '',
            retailerName: initialValuesProp?.retailerName || '',
            retailerLocaleCode: initialValuesProp?.retailerLocaleCode || '',
            retailerUrl: initialValuesProp?.retailerUrl || '',
            retailerDescription: initialValuesProp?.retailerDescription || '',
            selectedRetailerCourierIds: initialValuesProp?.selectedRetailerCourierIds || [],
            selectedRetailerId: initialValuesProp?.selectedRetailerId || '',
            isNewCourier: initialValuesProp?.isNewCourier || false,
            courierLocaleCode: initialValuesProp?.courierLocaleCode || '',
            selectedCourierId: initialValuesProp?.selectedCourierId || '',
            courierName: initialValuesProp?.courierName || '',
            courierUrl: initialValuesProp?.courierUrl || '',
            courierComments: initialValuesProp?.courierComments || '',
            newLocaleCode: initialValuesProp?.newLocaleCode || '',
            isoLocales: initialValuesProp?.isoLocales || []
        },
        onSubmit: async (values) => {
            setHasSubmitted(true);
            setServerErrors({});

            try {
                // Wait for reCAPTCHA and execute
                const grecaptcha = await waitForRecaptcha();
                const token = await grecaptcha.execute(recaptchaSiteKey, { action: 'submit' });

                // Get Anti-Forgery Token
                const antiForgeryToken = getAntiForgeryToken();

                // Build a classic form submission to work with ASP.NET Core MVC/Pages
                const tempForm = document.createElement('form');
                tempForm.method = 'post';
                tempForm.action = window.location.pathname + window.location.search;
                tempForm.style.display = 'none';

                // Add required fields
                addFormField(tempForm, '__RequestVerificationToken', antiForgeryToken);
                addFormField(tempForm, 'SubmissionType', values.submissionType);
                addFormField(tempForm, 'RecaptchaResponse', token);

                // Add fields based on submission type
                if (values.submissionType === 'retailer') {
                    addFormField(tempForm, 'RetailerName', values.retailerName);
                    addFormField(tempForm, 'RetailerLocaleCode', values.retailerLocaleCode);
                    addFormField(tempForm, 'RetailerUrl', values.retailerUrl);
                    addFormField(tempForm, 'RetailerDescription', values.retailerDescription);
                    addFormFields(tempForm, 'SelectedRetailerCourierIds', values.selectedRetailerCourierIds);
                } else if (values.submissionType === 'courier') {
                    addFormField(tempForm, 'SelectedRetailerId', values.selectedRetailerId);
                    addFormField(tempForm, 'CourierLocaleCode', values.courierLocaleCode);
                    addFormField(tempForm, 'IsNewCourier', values.isNewCourier.toString());
                    
                    if (values.isNewCourier) {
                        addFormField(tempForm, 'CourierName', values.courierName);
                        addFormField(tempForm, 'CourierUrl', values.courierUrl);
                    } else {
                        addFormField(tempForm, 'SelectedCourierId', values.selectedCourierId);
                    }
                    
                    addFormField(tempForm, 'CourierComments', values.courierComments);
                } else if (values.submissionType === 'locale') {
                    addFormField(tempForm, 'NewLocaleCode', values.newLocaleCode);
                }

                // Append form to body and submit
                document.body.appendChild(tempForm);
                tempForm.submit();
            } catch (error) {
                console.error('Error submitting form:', error);
                setServerErrors({ _general: ['An error occurred while submitting the form. Please try again.'] });
                setHasSubmitted(false);
            }
        }
    });

    // Data state
    const [locales, setLocales] = useState<Locale[]>([]);
    const [retailers, setRetailers] = useState<Retailer[]>([]);
    const [couriers, setCouriers] = useState<Courier[]>([]);
    const [retailerCouriers, setRetailerCouriers] = useState<Courier[]>([]);
    const [loadingCouriers, setLoadingCouriers] = useState(false);

    // Load reCAPTCHA script if not already loaded
    useEffect(() => {
        const loadRecaptchaScript = () => {
            if (getGrecaptcha()) return;

            const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
            if (existingScript) return;

            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        };

        loadRecaptchaScript();
    }, [recaptchaSiteKey]);

    // Load initial data
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [localesRes, retailersRes] = await Promise.all([
                    fetch('/Submit?handler=ApiLocales'),
                    fetch('/Submit?handler=ApiRetailers')
                ]);

                const localesData = await localesRes.json();
                const retailersData = await retailersRes.json();

                setLocales(localesData);
                setRetailers(retailersData);
            } catch (error) {
                console.error('Error loading initial data:', error);
            }
        };

        loadInitialData();
    }, []);

    const loadCouriersForRetailer = useCallback(async (retailerId: string) => {
        if (!retailerId) {
            setCouriers([]);
            return;
        }

        setLoadingCouriers(true);
        try {
            const response = await fetch(`/Submit?handler=ApiCouriers&retailerId=${retailerId}`);
            const data = await response.json();
            setCouriers(data);

            // Update courierLocaleCode if retailer's locale is known
            const retailer = retailers.find(r => r.id === retailerId);
            if (retailer?.localeCode) {
                formik.setFieldValue('courierLocaleCode', retailer.localeCode);
            }
        } catch (error) {
            console.error('Error loading couriers:', error);
            setCouriers([]);
        } finally {
            setLoadingCouriers(false);
        }
    }, [retailers, formik.setFieldValue]);

    const loadCouriersForLocale = useCallback(async (localeCode: string, isForRetailerFlow: boolean = true) => {
        if (!localeCode) {
            if (isForRetailerFlow) {
                setRetailerCouriers([]);
            } else {
                setCouriers([]);
            }
            return;
        }

        if (!isForRetailerFlow) {
            setLoadingCouriers(true);
        }

        try {
            const response = await fetch(`/Submit?handler=ApiCouriers&localeCode=${encodeURIComponent(localeCode)}`);
            const data = await response.json();
            if (isForRetailerFlow) {
                setRetailerCouriers(data);
            } else {
                setCouriers(data);
            }
        } catch (error) {
            console.error('Error loading couriers for locale:', error);
            if (isForRetailerFlow) {
                setRetailerCouriers([]);
            } else {
                setCouriers([]);
            }
        } finally {
            if (!isForRetailerFlow) {
                setLoadingCouriers(false);
            }
        }
    }, []);

    // Load couriers if retailer is pre-selected
    useEffect(() => {
        if (initialValuesProp?.selectedRetailerId) {
            loadCouriersForRetailer(initialValuesProp.selectedRetailerId);
        }
    }, [initialValuesProp?.selectedRetailerId, loadCouriersForRetailer]);

    // Handle retailer selection change (for courier submission)
    useEffect(() => {
        if (formik.values.submissionType === 'courier' && formik.values.selectedRetailerId) {
            loadCouriersForRetailer(formik.values.selectedRetailerId);
        }
    }, [formik.values.selectedRetailerId, formik.values.submissionType, loadCouriersForRetailer]);

    // Handle locale selection change (for retailer submission)
    useEffect(() => {
        if (formik.values.submissionType === 'retailer' && formik.values.retailerLocaleCode) {
            loadCouriersForLocale(formik.values.retailerLocaleCode);
        }
    }, [formik.values.retailerLocaleCode, formik.values.submissionType, loadCouriersForLocale]);

    // Handle locale selection change (for courier submission)
    useEffect(() => {
        if (formik.values.submissionType === 'courier' && formik.values.courierLocaleCode) {
            loadCouriersForLocale(formik.values.courierLocaleCode, false);
        }
    }, [formik.values.courierLocaleCode, formik.values.submissionType, loadCouriersForLocale]);

    const handleRetailerCourierToggle = (courierId: string) => {
        const currentIds = formik.values.selectedRetailerCourierIds;
        const newIds = currentIds.includes(courierId)
            ? currentIds.filter((id: string) => id !== courierId)
            : [...currentIds, courierId];
        formik.setFieldValue('selectedRetailerCourierIds', newIds);
    };

    const getAntiForgeryToken = (): string => {
        let tokenElement = document.querySelector('input[name="__RequestVerificationToken"]') as HTMLInputElement;
        return tokenElement?.value || '';
    };

    // Map server field names (PascalCase) to client field names (camelCase)
    const getServerError = useCallback((fieldName: string): string | undefined => {
        if (serverErrors[fieldName]?.[0]) {
            return serverErrors[fieldName][0];
        }
        const pascalCase = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
        if (serverErrors[pascalCase]?.[0]) {
            return serverErrors[pascalCase][0];
        }
        const fieldNameMap: Record<string, string> = {
            'submissionType': 'SubmissionType',
            'retailerName': 'RetailerName',
            'retailerLocaleCode': 'RetailerLocaleCode',
            'retailerUrl': 'RetailerUrl',
            'retailerDescription': 'RetailerDescription',
            'selectedRetailerId': 'SelectedRetailerId',
            'courierLocaleCode': 'CourierLocaleCode',
            'selectedCourierId': 'SelectedCourierId',
            'courierName': 'CourierName',
            'courierComments': 'CourierComments',
            'newLocaleCode': 'NewLocaleCode'
        };
        const serverFieldName = fieldNameMap[fieldName] || pascalCase;
        if (serverErrors[serverFieldName]?.[0]) {
            return serverErrors[serverFieldName][0];
        }
        return undefined;
    }, [serverErrors]);

    const getFieldError = useCallback((fieldName: string): string | undefined => {
        const errors = formik.errors as Record<string, string | undefined>;
        return errors[fieldName] || getServerError(fieldName);
    }, [formik.errors, getServerError]);

    const hasError = useCallback((fieldName: string): boolean => {
        const errors = formik.errors as Record<string, string | undefined>;
        const touched = formik.touched as Record<string, boolean | undefined>;
        const formikError = errors[fieldName];
        const serverError = getServerError(fieldName);
        return !!(formikError || (touched[fieldName] && formikError) || serverError);
    }, [formik.errors, formik.touched, getServerError]);

    const localeOptions = useMemo(() => 
        locales.map(locale => ({ value: locale.code, label: locale.name })),
        [locales]
    );

    const retailerOptions = useMemo(() => 
        retailers.map(retailer => {
            const label = retailer.localeName ? retailer.name + ' (' + retailer.localeName + ')' : retailer.name;
            return { value: retailer.id, label };
        }),
        [retailers]
    );

    const courierOptions = useMemo(() => 
        couriers.map(courier => {
            const label = courier.localeName ? courier.name + ' (' + courier.localeName + ')' : courier.name;
            return { value: courier.id, label };
        }),
        [couriers]
    );

    return (
        <div className="bg-light wsi-bg-light-gradient min-vh-100">
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col lg={8}>
                        <div className="text-center mb-5">
                            <h1 className="display-5 fw-bold text-dark wsi-letter-spacing-tight">Share some info</h1>
                            <p className="lead text-secondary">Spotted a courier we don't have listed? Help the community by adding it below.</p>
                        </div>

                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <Card.Body className="p-4 p-md-5">
                            <form onSubmit={formik.handleSubmit} noValidate>
                                {serverErrors._general && (
                                    <Alert variant="danger" className="rounded-4 border-0 shadow-sm mb-4" role="alert">
                                        <div className="d-flex align-items-center">
                                            <i className="bi bi-exclamation-circle-fill me-2 fs-5"></i>
                                            <ul className="mb-0 px-0 list-unstyled">
                                                {serverErrors._general.map((error) => (
                                                    <li key={error}>{error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </Alert>
                                )}

                                <div className="mb-5">
                                    <h5 className="fw-bold mb-4 text-dark">What are you adding?</h5>
                                    <RadioGroup
                                        name="submissionType"
                                        legend="Submission Type"
                                        options={[
                                            {
                                                id: 'submissionType-retailer',
                                                value: 'retailer',
                                                label: 'A new store',
                                                description: 'Add a retailer that isn\'t currently in our database'
                                            },
                                            {
                                                id: 'submissionType-courier',
                                                value: 'courier',
                                                label: 'A courier for a store',
                                                description: 'Add a delivery company that a store uses'
                                            },
                                            {
                                                id: 'submissionType-locale',
                                                value: 'locale',
                                                label: 'A new country',
                                                description: 'Suggest a new country for us to support'
                                            }
                                        ]}
                                        value={formik.values.submissionType}
                                        onChange={formik.handleChange}
                                        required
                                        error={getFieldError('submissionType')}
                                    />
                                </div>

                                <div className="mt-4 pt-4 border-top">
                                    {formik.values.submissionType === 'retailer' && (
                                        <RetailerFields
                                            formik={formik}
                                            getFieldError={getFieldError}
                                            hasError={hasError}
                                            localeOptions={localeOptions}
                                            retailerCouriers={retailerCouriers}
                                            handleRetailerCourierToggle={handleRetailerCourierToggle}
                                        />
                                    )}

                                    {formik.values.submissionType === 'courier' && (
                                        <CourierFields
                                            formik={formik}
                                            getFieldError={getFieldError}
                                            hasError={hasError}
                                            retailerOptions={retailerOptions}
                                            localeOptions={localeOptions}
                                            courierOptions={courierOptions}
                                            loadingCouriers={loadingCouriers}
                                        />
                                    )}

                                    {formik.values.submissionType === 'locale' && (
                                        <LocaleFields
                                            formik={formik}
                                            getFieldError={getFieldError}
                                            hasError={hasError}
                                            isoLocales={formik.values.isoLocales || []}
                                        />
                                    )}
                                </div>

                                {formik.values.submissionType && (
                                    <div className="d-flex flex-column flex-sm-row gap-3 mt-5 pt-4 border-top">
                                        <Button 
                                            type="submit" 
                                            variant="primary" 
                                            size="lg"
                                            className="rounded-pill px-5 fw-bold shadow-sm"
                                            disabled={hasSubmitted}
                                        >
                                            {hasSubmitted && (
                                                <Spinner animation="border" size="sm" className="me-2" role="status" aria-hidden="true" />
                                            )}
                                            {hasSubmitted ? 'Sending...' : 'Send Details'}
                                        </Button>
                                        <Button 
                                            href="/" 
                                            variant="light" 
                                            size="lg"
                                            className="rounded-pill px-4 text-secondary border"
                                            as="a"
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                )}
                            </form>
                        </Card.Body>
                    </Card>
                    
                    <div className="text-center mt-4">
                        <p className="text-muted small">
                            We review every submission manually to keep the data clean. Thanks for your help!
                        </p>
                    </div>
                </Col>
            </Row>
        </Container>
    </div>
    );
};

export function initSubmitForm(container: HTMLElement, props: SubmitFormProps) {
    const root = createRoot(container);
    root.render(<SubmitForm {...props} />);
}
